import axios from 'axios';

/**
 * Midus catalog ordering + default new-chat resolution (parity with native `HeaderModelMenuBuilder` / `resolvedDefaultRow`).
 */

export const HIDDEN_MODELS = ['chatgpt-image-latest', 'o4-mini-deep-research'];

const MIDUS_PREFIX = 'midus';

export function isMidusModel(displayName) {
  const name = displayName?.toLowerCase().trim();
  return Boolean(name && name.startsWith(MIDUS_PREFIX));
}

export function isVariant(displayName) {
  const l = (displayName || '').toLowerCase();
  return l.includes('fast') || l.includes('max') || l.includes('lite');
}

/**
 * @param {Array<{ provider: string, model: string, displayName?: string, enabled?: boolean, isPro?: boolean }>} availableModels
 * @returns {Array<{ provider: string, model: string, displayName: string, id: string, isPro?: boolean }>}
 */
export function buildMidusPickerRows(availableModels) {
  if (!Array.isArray(availableModels)) return [];
  const enabled = availableModels.filter((m) => m.enabled !== false);
  const allMidus = enabled.filter(
    (m) => isMidusModel(m.displayName) && !HIDDEN_MODELS.includes(m.model)
  );
  allMidus.sort((a, b) => {
    const aIsDefault = !isVariant(a.displayName);
    const bIsDefault = !isVariant(b.displayName);
    if (aIsDefault !== bIsDefault) return aIsDefault ? -1 : 1;
    const ad = (a.displayName || '').toLowerCase();
    const bd = (b.displayName || '').toLowerCase();
    if (ad.includes('max') && !bd.includes('max')) return -1;
    if (bd.includes('max') && !ad.includes('max')) return 1;
    const aFastLite = ad.includes('lite') || ad.includes('fast');
    const bFastLite = bd.includes('lite') || bd.includes('fast');
    if (aFastLite && !bFastLite) return 1;
    if (bFastLite && !aFastLite) return -1;
    return ad.localeCompare(bd);
  });
  return allMidus.map((m) => {
    const d = (m.displayName && String(m.displayName).trim()) || m.model;
    return {
      provider: m.provider,
      model: m.model,
      displayName: d,
      id: `${m.provider}:${m.model}`,
      isPro: m.isPro,
    };
  });
}

function implicitFastDefaultId(rows) {
  const isFast = (r) => {
    const t = r.displayName.toLowerCase();
    const m = r.model.toLowerCase();
    return t.includes('fast') || m.includes('fast');
  };
  const hit = rows.find(isFast);
  if (hit) return hit.id;
  if (rows.length > 1) {
    const rest = rows.slice(1);
    const lite = rest.find((r) => r.displayName.toLowerCase().includes('lite'));
    if (lite) return lite.id;
    const max = rest.find((r) => r.displayName.toLowerCase().includes('max'));
    if (max) return max.id;
    return rest[0].id;
  }
  return rows[0]?.id ?? null;
}

/**
 * @param {ReturnType<typeof buildMidusPickerRows>} rows
 * @param {string | undefined | null} savedKey — `preferences.defaultChatModelKey`
 */
export function resolveEffectiveDefaultModelId(rows, savedKey) {
  if (!rows.length) return null;
  const valid = new Set(rows.map((r) => r.id));
  const k = typeof savedKey === 'string' ? savedKey.trim() : '';
  if (k && valid.has(k)) return k;
  return implicitFastDefaultId(rows);
}

/** Parse `preferences.defaultChatModelKey` (`provider:model`). */
export function splitProviderModelKey(key) {
  if (typeof key !== 'string' || !key.trim()) return null;
  const k = key.trim();
  const i = k.indexOf(':');
  if (i <= 0 || i >= k.length - 1) return null;
  return { provider: k.slice(0, i), model: k.slice(i + 1) };
}

function firstMidusPickerRow(availableModels) {
  const rows = buildMidusPickerRows(availableModels);
  const r = rows[0];
  return r ? { provider: r.provider, model: r.model } : null;
}

/**
 * PATCH `preferences.defaultChatModelKey` (`provider:model`).
 * @returns {Promise<object>} Updated user payload from API
 */
export async function patchDefaultChatModelKey(key) {
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Not authenticated');
  const { data } = await axios.patch(
    `${API_BASE_URL}/users/me`,
    { preferences: { defaultChatModelKey: key } },
    {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      timeout: 15000,
    }
  );
  return data;
}

/**
 * Merge PATCH /users/me response into client `user`.
 * Do not spread the full API payload: it often omits populated `company`, which would clear
 * `subscriptionPlan` and trigger `/waiting` in AppRoutes.
 */
export function mergeUserFromPatch(prevUser, patchResponse) {
  if (!patchResponse) return prevUser;
  if (!prevUser) return patchResponse;
  return {
    ...prevUser,
    id: patchResponse.id || patchResponse._id || prevUser.id,
    preferences: {
      ...(prevUser.preferences || {}),
      ...(patchResponse.preferences || {}),
    },
  };
}

const CHAT_INPUT_STORAGE_KEY = 'chat_input_state';

/** Concrete provider/model for implicit new-chat default (Fast when no saved preference). */
export function pickEffectiveDefaultChatModel(availableModels, savedPreferenceKey) {
  const rows = buildMidusPickerRows(availableModels);
  const id = resolveEffectiveDefaultModelId(rows, savedPreferenceKey);
  const row = rows.find((r) => r.id === id);
  if (!row) return null;
  return { provider: row.provider, id: row.model, displayName: row.displayName };
}

/**
 * New chat shell (no chatId): use saved default preference or Fast only — not the last chat's model in localStorage.
 */
export function resolveModelSelectionForNewChat(availableModels, savedPreferenceKey) {
  const pick = pickEffectiveDefaultChatModel(availableModels, savedPreferenceKey);
  if (pick) return { provider: pick.provider, model: pick.id };
  const first = firstMidusPickerRow(availableModels);
  if (first) return first;
  return { provider: 'auto', model: 'auto' };
}

/**
 * Restore explicit pick from localStorage, or fall back to effective default (not `auto` / 2.6 auto-label).
 */
export function resolveInitialChatModelSelection(availableModels, savedPreferenceKey) {
  let savedSelection = null;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(CHAT_INPUT_STORAGE_KEY) : null;
    savedSelection = raw ? JSON.parse(raw) : null;
  } catch {
    savedSelection = null;
  }

  if (savedSelection?.selectedProvider && savedSelection?.selectedModel) {
    const isLegacyAuto =
      savedSelection.selectedProvider === 'auto' && savedSelection.selectedModel === 'auto';
    if (!isLegacyAuto) {
      const exists = availableModels.some(
        (m) =>
          m.provider === savedSelection.selectedProvider &&
          m.model === savedSelection.selectedModel &&
          m.enabled !== false
      );
      if (exists) {
        return { provider: savedSelection.selectedProvider, model: savedSelection.selectedModel };
      }
    }
  }

  const pick = pickEffectiveDefaultChatModel(availableModels, savedPreferenceKey);
  if (pick) return { provider: pick.provider, model: pick.id };
  const first = firstMidusPickerRow(availableModels);
  if (first) return first;
  return { provider: 'auto', model: 'auto' };
}

/** Keep composer localStorage in sync after changing default model from the header. */
export function persistChatInputModelSelection(provider, model) {
  try {
    const cur = JSON.parse(localStorage.getItem(CHAT_INPUT_STORAGE_KEY) || '{}');
    localStorage.setItem(
      CHAT_INPUT_STORAGE_KEY,
      JSON.stringify({
        ...cur,
        selectedProvider: provider,
        selectedModel: model,
      })
    );
  } catch {
    /* ignore */
  }
}
